import { useMemo, useState } from 'react'
import {
  type InheritanceResource,
  type ReadableAccessMetadata,
  type UserLike,
} from '@opallabs/concepts'
import { getMemberCount } from './helpers'
import { useComputedAccess } from './useComputedAccessData'

type UseEditableAccessMembersArgs = Readonly<{
  accessMetadata?: ReadableAccessMetadata
  ownerId?: string
}>

/**
 * Wraps `useDerivedAccessMembers` to provide mutable state for optimistic updates.
 */
export function useEditableAccessMembers({
  accessMetadata,
  ownerId,
}: UseEditableAccessMembersArgs) {
  const derived = useComputedAccess({ accessMetadata, ownerId })

  const [collaborators, setCollaborators] = useState<UserLike[]>(
    derived.collaborators
  )
  const [inheritanceResources, setInheritanceResources] = useState<
    InheritanceResource[]
  >(derived.inheritanceResources)

  // @see https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevDerivedCollaborators, setPrevDerivedCollaborators] = useState(
    derived.collaborators
  )
  if (prevDerivedCollaborators !== derived.collaborators) {
    setPrevDerivedCollaborators(derived.collaborators)
    setCollaborators(derived.collaborators)
  }

  const [previousDerivedResources, setPreviousDerivedResources] = useState(
    derived.inheritanceResources
  )
  if (previousDerivedResources !== derived.inheritanceResources) {
    setPreviousDerivedResources(derived.inheritanceResources)
    setInheritanceResources(derived.inheritanceResources)
  }

  const addCollaborator = (collaborator: UserLike) => {
    setCollaborators((current) => [...current, collaborator])
  }

  const removeCollaborator = (collaborator: UserLike) => {
    setCollaborators((current) =>
      current.filter((c) => c.uuid !== collaborator.uuid)
    )
  }

  const setInheritance = (
    resource: InheritanceResource,
    inheritsMembership: boolean
  ) => {
    setInheritanceResources((current) => {
      const index = current.findIndex((r) => r.uuid === resource.uuid)
      if (index === -1) return current

      return [
        ...current.slice(0, index),
        { ...resource, inheritsMembership },
        ...current.slice(index + 1),
      ]
    })
  }

  const [memberCount, spacesCount] = useMemo(
    () =>
      getMemberCount(
        derived.owner
          ? [...collaborators, ...inheritanceResources, derived.owner]
          : [...collaborators, ...inheritanceResources]
      ),
    [collaborators, derived.owner, inheritanceResources]
  )

  return {
    ...derived,
    addCollaborator,
    collaborators,
    inheritanceResources,
    initialCollaborators: derived.collaborators,
    memberCount,
    removeCollaborator,
    setInheritance,
    spacesCount,
  }
}
